/** @jsx jsx */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import { LinkContainer } from 'react-router-bootstrap';
import Field from 'redux-form/lib/Field';
import moment from 'moment';
import styled from '@emotion/styled';
import { css, jsx } from '@emotion/core';
import { maxLength } from 'anchor-website-common/helpers/serverRenderingUtils';
import { useOptimizelyFeature } from 'hooks/useOptimizelyFeature';
import { EmailVerificationModal } from 'screens/SettingsScreen/components/EmailVerificationModal';
import { AnchorAPI } from 'modules/AnchorAPI';
import { useFeatureFlagsCtx } from 'contexts/FeatureFlags';
import { getIsEPEnabled } from 'modules/FeatureFlags';
import { InteractivePoll } from './components/InteractivePoll';
import ImageUploader from '../ImageUploader';
import Box from '../../shared/Box';
import Text from '../../shared/Text';
import { CenteredSpinner } from '../Spinner';
import DesktopGrid from '../DesktopGrid';
import { FieldInputWithRedux } from '../FieldInput';
import { FieldRadioToggleWithRedux } from '../FieldRadioToggle';
import { FieldLabel } from '../FieldLabel';
import { FieldToggleWithRedux } from '../FieldToggle';
import { FieldRichTextEditorContainer } from './components/FieldRichTextEditor/FieldRichTextEditorContainer';
import { HorizontalRule } from '../HorizontalRule';
import { DEFAULT_IMAGE } from '../../onboarding';
import events from './events/index';
import { UnpublishEpisodeErrorModal } from '../UnpublishEpisodeErrorModal';
import styles from './styles.sass';
import { ResendEmailWarning } from '../ResendEmailWarning';
import { Button as NewButton } from '../../shared/Button/NewButton';
import { DistributeToSpotifyModal } from './components/DistributeToSpotifyModal';
import {
  PublishEpisodeHeader,
  PublishMusicHeader,
  PublishDateContainer,
  FieldToggleContainer,
  SubscriptionImageLabel,
} from './styles';
import { FieldDatePicker } from '../FieldDatePicker';
import { PreDistributionModal } from '../../screens/PreDistributionModalScreen/PreDistributionModal';
import { MusicNotAllowedErrorModal } from './components/MusicNotAllowedErrorModal';

const maxLength200 = maxLength(200);
const maxLength4000 = maxLength(4000);
const emptyStringToNull = value => (value === '' ? null : value);
const UNVERIFIED = 'unverified'; // TODO: Extract this value from the UserVerificationState type in AnchorAPI.verifyUserEmail

export const EpisodeEditorPublishErrors = {
  MUSIC_NOT_ALLOWED: 'MUSIC_NOT_ALLOWED',
};

const InlineLabelStyles = css`
  margin-right: 40px;
  width: 150px;
`;

const NumberInputStyles = css`
  width: 74px;
  input {
    text-align: center;
  }
`;

const ErrorAlert = styled.div`
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  background-color: rgba(208, 2, 27, 0.1);
  font-size: 1.4rem;
  color: #e54751;
  margin: 12px 0;
`;

const getEpisodeState = values => {
  if (!values) return null;
  const { publishOn } = values;
  const now = moment();
  if (!publishOn) return 'isDraft';
  if (publishOn < now) return 'isPublished';
  if (publishOn > now) return 'isScheduled';
  return null;
};

const getFormatedTime = dateTime =>
  moment.utc(dateTime).local().format('M/D/YY [at] h:mm A');

const validateRequiredField = value => (value ? undefined : 'Required field');
const validateDescriptionField = value =>
  value && value !== '<p><br></p>' && value !== '<p></p>'
    ? undefined
    : 'Required field';

class EpisodeEditorPublishClassComponent extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      submitButtonClicked: null,
      doPersistError: false,
      isEmailVerificationModalOpen: false,
      emailVerificationRequestSent: false,
      canUnPublish: true,
      showUnpublishEpisodeErrorModal: false,
      isSpotifyDistributionModalOpen: false,
      isPreDistributionModalOpen: false,
    };
  }

  componentDidMount() {
    const {
      match: { path, params },
      actions: {
        fetchMyPodcastMetadata,
        receiveLocalUploadedImage,
        fetchUserVerificationState,
      },
      podcastEditor: { podcastImage },
    } = this.props;
    AnchorAPI.fetchPodcastEpisodeList({ limit: 1000, page: 1 }).then(
      ({ episodes }) => {
        const now = new Date();
        const publishedEpisodes = episodes.filter(episode => {
          // Unpublishing the only RSS episode breaks the user's feed on external platforms
          // Users can unpublish TAXI episodes without breaking their RSS feed
          const { publishOn, containsMusicSegments } = episode;
          return (
            publishOn && new Date(publishOn) < now && !containsMusicSegments
          );
        });
        if (publishedEpisodes.length === 1) {
          if (params.podcastEpisodeId === publishedEpisodes[0].episodeId) {
            this.setState({ canUnPublish: false });
          }
        }
      }
    );
    receiveLocalUploadedImage(null);
    fetchUserVerificationState();
    const requestedPodcastEpisodeId =
      (params && params.podcastEpisodeId) || null;
    if (!podcastImage) {
      fetchMyPodcastMetadata();
    }

    this.setState({
      isPublishingForTheFirstTime: path === '/dashboard/episode/new/publish',
    });

    if (requestedPodcastEpisodeId) {
      events.trackMetadataEditPageView();
    } else {
      events.publishEpisodePageViewed();
    }
  }

  getDraftButtonText = initialEpisodeState => {
    const { isPublishingForTheFirstTime } = this.state;
    switch (initialEpisodeState) {
      case 'isDraft':
        return isPublishingForTheFirstTime
          ? 'Save as a draft'
          : 'Update saved draft';
      case 'isScheduled':
        return 'Revert to draft';
      case 'isPublished':
        return 'Revert to draft';
      default:
        return null;
    }
  };

  getPublishButtonText = initialEpisodeState => {
    const { isPublishingForTheFirstTime } = this.state;
    const {
      formValues,
      initialValues: { publishOn: initialPublishOn },
    } = this.props;
    if (!formValues) return null;
    const { publishOn } = formValues;
    const isPublishOnValueUpdated = !moment(initialPublishOn).isSame(publishOn);
    const isPublishOnValueNeverSet = !publishOn && !initialPublishOn;
    switch (initialEpisodeState) {
      case 'isDraft':
        if (isPublishOnValueNeverSet) return 'Publish now';
        if (isPublishingForTheFirstTime) {
          return publishOn ? 'Schedule episode' : 'Publish now';
        }
        return isPublishOnValueUpdated ? 'Schedule episode' : 'Publish now';
      case 'isScheduled':
        return publishOn ? 'Update episode' : 'Publish now';
      case 'isPublished':
        return publishOn && isPublishOnValueUpdated
          ? 'Schedule episode'
          : 'Update episode';
      default:
        return 'Update episode';
    }
  };

  onOpenEmailVerificationModal = () => {
    const { userId } = this.props;
    AnchorAPI.resendVerificationEmail({ userId });
  };

  getDefaultSubmitType = () => {
    const { formValues } = this.props;
    if (!formValues) return 'draft';
    return !formValues.publishOn ? 'draft' : 'publish';
  };

  handleClickDismissAudioErrorModal = () => {
    const {
      actions: { dismissAudioError },
    } = this.props;
    if (dismissAudioError) dismissAudioError();
    this.setState({
      doPersistError: true,
    });
  };

  handleShowAudioErrorModal = () => {
    const { onEpisodeEditorPopupView } = this.props;
    onEpisodeEditorPopupView('add_audio_error');
  };

  handleShowCropperModal = () => {
    const { onEpisodeEditorPopupView } = this.props;
    onEpisodeEditorPopupView('image_editor');
  };

  renderSubHeaderContent = initialEpisodeState => {
    const { isPublishingForTheFirstTime } = this.state;
    const {
      initialValues: { created, publishOn },
    } = this.props;
    switch (initialEpisodeState) {
      case 'isDraft':
        return isPublishingForTheFirstTime ? null : (
          <Text color="#5f6369" size="lg">
            Created on: {getFormatedTime(created)}
          </Text>
        );
      case 'isScheduled':
        return (
          <Text color="#5f6369" size="lg">
            Scheduled for: {getFormatedTime(publishOn)}
          </Text>
        );
      case 'isPublished':
        return (
          <Text color="#5f6369" size="lg">
            Published on: {getFormatedTime(publishOn)}
          </Text>
        );
      default:
        return null;
    }
  };

  isUnverified = () => {
    const { userVerificationState } = this.props;
    return userVerificationState === UNVERIFIED;
  };

  renderDraftButton = initialEpisodeState => {
    const { submitButtonClicked, doPersistError } = this.state;
    const { submitting } = this.props;
    return (
      <div className={styles.draftButtonWrapper}>
        <NewButton
          kind="button"
          type="submit"
          css={css`
            width: 270px;
          `}
          onClick={this.handleSaveDraft()}
          isDisabled={submitting || doPersistError}
        >
          {submitButtonClicked === 'draft' && submitting
            ? 'Saving...'
            : this.getDraftButtonText(initialEpisodeState)}
        </NewButton>
      </div>
    );
  };

  isPodcastSetup = () => {
    const {
      podcastEditor: {
        podcastName,
        podcastDescription,
        language,
        itunesCategory,
        podcastImage,
      },
    } = this.props;
    return (
      podcastName &&
      podcastDescription &&
      language &&
      itunesCategory &&
      podcastImage
    );
  };

  renderSaveButtonSpotifyExclusive = () => {
    const {
      userId,
      submitting,
      spotifyDistributionStatus,
      isMusicAndTalkFastTrackEnabled,
      containsMusicSegments,
    } = this.props;
    // Only music episodes need review before publishing
    // Partnered M+T shows do not (isMusicAndTalkFastTrackEnabled)
    const needsReview =
      containsMusicSegments && !isMusicAndTalkFastTrackEnabled;
    const { submitButtonClicked } = this.state;
    const idleButtonCopy = needsReview
      ? 'Submit episode for review'
      : 'Publish';
    return (
      <NewButton
        kind="button"
        color="purple"
        onClick={async e => {
          e.persist();
          try {
            const {
              externalUrls: { spotify },
              podcastDistributionStatus,
            } = await AnchorAPI.fetchDistributionData({
              currentUserId: userId,
            });
            if (
              podcastDistributionStatus === 'noActionTaken' ||
              !this.isPodcastSetup()
            ) {
              return this.setState({ isPreDistributionModalOpen: true });
            }
            if (
              !spotify ||
              (spotifyDistributionStatus === 'off' &&
                podcastDistributionStatus === 'optedout')
            ) {
              return this.setState({ isSpotifyDistributionModalOpen: true });
            }
            return this.handlePublish()(e);
          } catch (err) {
            throw new Error(err.message);
          }
        }}
        css={css`
          width: 270px;
          flex: 1;
        `}
      >
        {submitButtonClicked === 'publish' && submitting
          ? 'Submitting...'
          : idleButtonCopy}
      </NewButton>
    );
  };

  renderSaveButton = initialEpisodeState => {
    const {
      submitButtonClicked,
      doPersistError,
      emailVerificationRequestSent,
      canUnPublish,
    } = this.state;
    const {
      submitting,
      episodeEditorScreen: { episodeAudios, podcastEpisodeId },
      initialValues,
      onSubmit,
      handleSubmit,
      formValues: { publishOn },
      userId,
    } = this.props;
    return (
      <div className={styles.saveButtonWrapper}>
        <NewButton
          kind="button"
          color="purple"
          type="submit"
          css={css`
            width: 270px;
          `}
          onClick={handleSubmit(values => {
            const now = new Date();
            const isScheduled = moment(publishOn) > now;
            if (!canUnPublish && isScheduled) {
              this.setState({ showUnpublishEpisodeErrorModal: true });
              return null;
            }
            if (!episodeAudios.length) return null;
            const isUnverified = this.isUnverified();
            this.setState(
              {
                submitButtonClicked: 'publish',
                isEmailVerificationModalOpen: isUnverified,
                emailVerificationRequestSent: isUnverified,
                isPublishingForTheFirstTime: !isUnverified,
              },
              () => {
                const isBackgroundAudioPresent = episodeAudios.some(audio => {
                  const {
                    audioTransformationName,
                    url,
                    derivedFromAudioId,
                  } = audio;
                  return (
                    url &&
                    audioTransformationName === 'backgroundTrack2' &&
                    Boolean(derivedFromAudioId)
                  );
                });
                // only fire publish events if an episode is going from non-published to published
                // aka isDraft === true or it doesn't have a publishOn date already
                if (
                  (!this.isUnverified() && values.isDraft) ||
                  !values.publishOn
                ) {
                  events.episodePublished(userId);
                  if (isBackgroundAudioPresent) {
                    events.episodePublishedWithBackgroundTracks({
                      podcastEpisodeId,
                    });
                  }
                }
              }
            );
            if (this.isUnverified()) return null;
            return onSubmit(
              {
                ...values,
                isDraft: false,
              },
              initialValues
            );
          })}
          isDisabled={
            submitting ||
            doPersistError ||
            !episodeAudios.length ||
            emailVerificationRequestSent
          }
        >
          {submitButtonClicked === 'publish' && submitting
            ? 'Publishing...'
            : `${this.getPublishButtonText(initialEpisodeState)}`}
        </NewButton>
      </div>
    );
  };

  handlePublish = () => {
    const { initialValues, handleSubmit, onSubmit } = this.props;
    return handleSubmit(values => {
      // todo change submitButtonClicked
      this.setState({ submitButtonClicked: 'publish' }, () => {
        // todo change location
        events.publishMenuButtonClicked({ location: 'episode_edit' });
      });
      return onSubmit(
        {
          ...values,
          isDraft: false,
        },
        initialValues
      );
    });
  };

  handleSaveDraft = () => {
    const { canUnPublish, isPublishingForTheFirstTime } = this.state;
    const { initialValues, handleSubmit, onSubmit } = this.props;
    return handleSubmit(values => {
      if (!canUnPublish) {
        this.setState({ showUnpublishEpisodeErrorModal: true });
        return null;
      }
      this.setState({ submitButtonClicked: 'draft' }, () => {
        if (isPublishingForTheFirstTime) {
          // if in new publish view we want to fire off the event that has the location as 'episode_edit'
          // otherwise fire off event that has location as 'episode_metadata_edit'
          events.trackSaveAsDraftButtonClicked();
        } else {
          events.trackMetadataEditSaveAsDraftButtonClicked();
        }
      });
      return onSubmit(
        {
          ...values,
          isDraft: true,
          publishOn: null,
        },
        initialValues
      );
    });
  };

  render() {
    const {
      actions: { receiveLocalUploadedImage, setPublishOnValue },
      form,
      error,
      clearSubmitErrors,
      episodeEditorScreen: {
        podcastEpisodeId,
        addAudioError,
        isFetchingEpisode,
        metadata: { episodeImage },
      },
      formValues,
      handleSubmit,
      onSubmit,
      podcastEditor: { podcastImage },
      initialValues,
      userId,
      containsMusicSegments,
      stationId,
      isPaywallsEnabled,
      isSpotifyExclusive,
      isPollsEnabled,
    } = this.props;
    const {
      isEmailVerificationModalOpen,
      showUnpublishEpisodeErrorModal,
      isSpotifyDistributionModalOpen,
      isPreDistributionModalOpen,
    } = this.state;
    const initialEpisodeState = getEpisodeState(initialValues);
    const defaultSubmitType = this.getDefaultSubmitType();
    return (
      <DesktopGrid className={styles.grid}>
        {this.isUnverified() && (
          <div className={styles.resendEmailContainer}>
            <ResendEmailWarning userId={userId} />
          </div>
        )}
        <form
          className={styles.form}
          onSubmit={handleSubmit(values => {
            this.setState({ submitButtonClicked: defaultSubmitType }, () => {
              events.publishMenuButtonClicked({ location: 'episode_edit' });
            });
            return onSubmit(
              {
                ...values,
                isDraft: defaultSubmitType === 'draft',
              },
              initialValues
            );
          })}
        >
          {isFetchingEpisode || !formValues ? (
            <CenteredSpinner color="gray" />
          ) : (
            <React.Fragment>
              <div className={styles.mainOptionsSection}>
                {isSpotifyExclusive ? (
                  <div>
                    <PublishMusicHeader>
                      <div className={styles.headerContainer}>
                        <h1 className={styles.header}>Submit to Spotify</h1>
                      </div>
                      <div className={styles.desktopButtonContainer}>
                        {this.renderDraftButton(initialEpisodeState)}
                        {this.renderSaveButtonSpotifyExclusive()}
                      </div>
                    </PublishMusicHeader>
                    {containsMusicSegments && (
                      <p
                        css={css`
                          margin-bottom: 24px;
                          font-size: 1.6rem;
                        `}
                      >
                        Since this episode contains songs, it will only be
                        available on Spotify, and needs to be reviewed before it
                        goes live. We’ll let you know as soon as it’s approved.
                        If you schedule this episode for a future date, we’ll
                        review your episode as quickly as possible, but can’t
                        guarantee it will be done before your scheduled
                        publishing date/time.
                      </p>
                    )}
                  </div>
                ) : (
                  <PublishEpisodeHeader>
                    <div className={styles.headerContainer}>
                      <h1 className={styles.header}>Episode options</h1>
                      <Box marginTop={8}>
                        {this.renderSubHeaderContent(initialEpisodeState)}
                      </Box>
                    </div>
                    <div className={styles.desktopButtonContainer}>
                      {this.renderDraftButton(initialEpisodeState)}
                      {this.renderSaveButton(initialEpisodeState)}
                    </div>
                  </PublishEpisodeHeader>
                )}
                {error &&
                  error !== EpisodeEditorPublishErrors.MUSIC_NOT_ALLOWED && (
                    <ErrorAlert>
                      <span>
                        We're unable to publish your episode. Revert to draft to
                        make any edits and submit again.
                      </span>
                    </ErrorAlert>
                  )}
                {error === EpisodeEditorPublishErrors.MUSIC_NOT_ALLOWED && (
                  <MusicNotAllowedErrorModal onDismiss={clearSubmitErrors} />
                )}
                <Box display="flex" direction="column">
                  <Box marginBottom={18}>
                    <FieldLabel htmlFor="title">
                      <Label>Episode title*</Label>
                    </FieldLabel>
                    <Field
                      name="title"
                      type="text"
                      component={FieldInputWithRedux}
                      maxCharacterLength={200}
                      normalize={maxLength200}
                      placeholder="What do you want to call this episode?"
                      validate={validateRequiredField}
                    />
                  </Box>
                  <Box marginBottom={18}>
                    <Field
                      label="Episode description*"
                      name="description"
                      type="textarea"
                      component={FieldRichTextEditorContainer}
                      maxCharacterLength={4000}
                      normalize={maxLength4000}
                      placeholder="What else do you want your listeners to know?"
                      validate={validateDescriptionField}
                    />
                  </Box>
                  <PublishDateContainer>
                    <FieldDatePicker
                      label="Publish date:"
                      form={form}
                      value={formValues.publishOn}
                      setValue={setPublishOnValue}
                    />
                    {isPaywallsEnabled && !containsMusicSegments && (
                      <FieldToggleContainer>
                        <Field
                          name="isPW"
                          label="Subscription only"
                          component={FieldToggleWithRedux}
                          onChange={() => {
                            events.episodeSubscriptionToggled({
                              podcastEpisodeId,
                              toggledOn: !formValues.isPW,
                            });
                          }}
                        />
                        <FieldLabel
                          htmlFor="isPW"
                          css={css`
                            color: #292f36;
                            font-size: 16px;
                            font-weight: 500;
                          `}
                        >
                          Subscription only
                        </FieldLabel>
                      </FieldToggleContainer>
                    )}
                  </PublishDateContainer>
                  <div className={styles.mobileButtonContainer}>
                    {this.renderDraftButton(initialEpisodeState)}
                    {this.renderSaveButton(initialEpisodeState)}
                  </div>
                </Box>
              </div>
              <div className={styles.ruleWrapper}>
                <HorizontalRule marginTop={46} marginBottom={46} />
              </div>
              {podcastEpisodeId && isPollsEnabled && (
                <InteractivePoll episodeId={podcastEpisodeId} userId={userId} />
              )}
              <div className={styles.customizeOptionsSection}>
                <div className={styles.customizeOptionsHeaderContainer}>
                  <Box marginRight={6}>
                    <h2 className={styles.customizeOptionsHeader}>
                      Customize this episode
                    </h2>
                  </Box>
                  <Text color="#7f8287" size="md">
                    (Optional)
                  </Text>
                </div>
                <div className={styles.customizeOptionsFormContainer}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    dangerouslySetInlineStyle={{ flex: 1 }}
                  >
                    <div style={{ position: 'relative', maxWidth: 250 }}>
                      <ImageUploader
                        className={styles.editImageContainer}
                        onImageLocallyUploaded={receiveLocalUploadedImage}
                        originalImage={
                          episodeImage || podcastImage || DEFAULT_IMAGE
                        }
                        uploadLinkLabel="Upload new episode art"
                        onShowCropper={this.handleShowCropperModal}
                        screen="episode_edit"
                        clickEventName="change_image_clicked"
                      />
                      {isPaywallsEnabled &&
                        !containsMusicSegments &&
                        formValues.isPW && (
                          <SubscriptionImageLabel>
                            Subscription
                          </SubscriptionImageLabel>
                        )}
                    </div>
                  </Box>
                  <div className={styles.customizeOptionsFieldContainer}>
                    <InlineField>
                      <FieldLabel
                        htmlFor="podcastSeasonNumber"
                        cssProp={InlineLabelStyles}
                      >
                        <Label>Season number</Label>
                      </FieldLabel>
                      <Field
                        name="podcastSeasonNumber"
                        type="number"
                        cssProp={NumberInputStyles}
                        component={FieldInputWithRedux}
                        min={0}
                        normalize={emptyStringToNull}
                        labelWidth={160}
                        isInline
                      />
                    </InlineField>
                    <InlineField>
                      <FieldLabel
                        htmlFor="podcastEpisodeNumber"
                        cssProp={InlineLabelStyles}
                      >
                        <Label>Episode number</Label>
                      </FieldLabel>
                      <Field
                        label="Episode number"
                        name="podcastEpisodeNumber"
                        type="number"
                        cssProp={NumberInputStyles}
                        component={FieldInputWithRedux}
                        min={0}
                        normalize={emptyStringToNull}
                        labelWidth={160}
                        isInline
                      />
                    </InlineField>
                    <Box marginBottom={30} maxWidth={369}>
                      <Field
                        label={<Label>Episode type</Label>}
                        name="podcastEpisodeType"
                        component={FieldRadioToggleWithRedux}
                        options={[
                          { label: 'Full', value: 'full' },
                          { label: 'Trailer', value: 'trailer' },
                          { label: 'Bonus', value: 'bonus' },
                        ]}
                      />
                    </Box>
                    <Box maxWidth={246}>
                      <Field
                        label={<Label>Content</Label>}
                        name="podcastEpisodeIsExplicit"
                        component={FieldRadioToggleWithRedux}
                        options={[
                          { label: 'Clean', value: false },
                          { label: 'Explicit', value: true },
                        ]}
                      />
                    </Box>
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
        </form>
        <Modal
          show={addAudioError && addAudioError.isModal}
          onEntered={this.handleShowAudioErrorModal}
        >
          {addAudioError && (
            <Modal.Body key="errorPrompt" className="text-center">
              <h2>{addAudioError.title}</h2>
              <p>{addAudioError.subTitle}</p>
            </Modal.Body>
          )}
          <Modal.Footer key="errorFooter">
            <LinkContainer to="/dashboard/episode/new/upload">
              <Button
                block
                bsStyle="primary"
                onClick={this.handleClickDismissAudioErrorModal}
              >
                Try uploading again
              </Button>
            </LinkContainer>
            <Button
              block
              bsStyle="link"
              onClick={this.handleClickDismissAudioErrorModal}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
        {isEmailVerificationModalOpen && (
          <EmailVerificationModal
            onOpen={this.onOpenEmailVerificationModal}
            userId={userId}
            onClickClose={this.handleSaveDraft()}
            type="publish"
          />
        )}
        {showUnpublishEpisodeErrorModal && (
          <UnpublishEpisodeErrorModal
            action="unpublish"
            onClose={() => {
              this.setState({ showUnpublishEpisodeErrorModal: false });
            }}
          />
        )}
        {isSpotifyDistributionModalOpen && (
          <DistributeToSpotifyModal
            onClickClose={e => {
              this.setState({ isSpotifyDistributionModalOpen: false });
              this.handleSaveDraft()(e);
            }}
            onSubmit={async e => {
              try {
                await AnchorAPI.requestSpotifyOnlyDistribution({
                  stationId,
                });
                // after distributing to Spotify we can submit the episode
                // call the form's submit function from the modal
                this.handlePublish()(e);
              } catch (err) {
                throw new Error(err.message);
              }
            }}
            podcastImage={podcastImage}
          />
        )}
        {isPreDistributionModalOpen && (
          <PreDistributionModal
            onHideModal={() =>
              this.setState({ isPreDistributionModalOpen: false })
            }
            onFinishAcceptDistribution={this.handlePublish()}
          />
        )}
      </DesktopGrid>
    );
  }
}

function EpisodeEditorPublish(props) {
  const { containsMusicSegments } = props;
  const {
    state: { featureFlags },
  } = useFeatureFlagsCtx();
  const [isPollsEnabled] = useOptimizelyFeature('episode_polls');
  if (!featureFlags) return null;
  const isEPEnabled = getIsEPEnabled(featureFlags);
  const isSpotifyExclusive = isEPEnabled || containsMusicSegments;
  return (
    <EpisodeEditorPublishClassComponent
      {...props}
      isSpotifyExclusive={isSpotifyExclusive}
      isPollsEnabled={isPollsEnabled}
    />
  );
}

const InlineField = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
`;

const Label = styled.h4`
  color: #292f36;
  font-size: 1.6rem;
  font-weight: normal;
  margin: 0 0 10px 0;
`;

EpisodeEditorPublishClassComponent.propTypes = {
  actions: PropTypes.shape({}).isRequired,
  form: PropTypes.string.isRequired,
  episodeEditorScreen: PropTypes.shape({}).isRequired,
  formValues: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({}).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  podcastEditor: PropTypes.shape({}).isRequired,
  initialValues: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEpisodeEditorPopupView: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
};

export { EpisodeEditorPublish };
